import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Paper,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Fade,
  Alert,
  Snackbar,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LockIcon from "@mui/icons-material/Lock";
import TableChartIcon from "@mui/icons-material/TableChart";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { GlassButtonLoading } from "../../themes/UiElements";

const COLUMN_TYPES = [
  "Text",
  "Number",
  "Date",
  "Email",
  "URL",
  "Boolean",
  "Dropdown",
];

const MANDATORY_COLUMNS = [
  {
    id: "mandatory-1",
    name: "phoneNumber",
    type: "Text",
    required: true,
    locked: true,
  },
  {
    id: "mandatory-2",
    name: "message",
    type: "Text",
    required: true,
    locked: true,
  },
];

let idCounter = 10;

export default function SheetSchemaBuilder() {
  const [sheetName, setSheetName] = useState("My Sheet");
  const [columns, setColumns] = useState(MANDATORY_COLUMNS);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "" });
  const [dragOver, setDragOver] = useState(null);
  const [dragging, setDragging] = useState(null);

  const addColumn = () => {
    setColumns((prev) => [
      ...prev,
      {
        id: `col-${++idCounter}`,
        name: "",
        type: "Text",
        required: false,
        locked: false,
      },
    ]);
  };

  const updateColumn = (id, field, value) => {
    setColumns((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const removeColumn = (id) => {
    setColumns((prev) => prev.filter((c) => c.id !== id));
  };

  const handleDragStart = (id) => setDragging(id);
  const handleDragOver = (e, id) => {
    e.preventDefault();
    setDragOver(id);
  };
  const handleDrop = (targetId) => {
    if (!dragging || dragging === targetId) return;
    const fromIdx = columns.findIndex((c) => c.id === dragging);
    const toIdx = columns.findIndex((c) => c.id === targetId);
    if (columns[fromIdx].locked || columns[toIdx].locked) return;
    const next = [...columns];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setColumns(next);
    setDragging(null);
    setDragOver(null);
  };

  const handleExport = () => {
    const schema = {
      sheetName,
      columns: columns.map(({ name, type, required }) => ({
        name,
        type,
        required,
      })),
    };
    navigator.clipboard.writeText(JSON.stringify(schema, null, 2));
    setSnackbar({ open: true, msg: "Schema copied to clipboard!" });
  };

  const allValid = columns.every((c) => c.name.trim() !== "");
  const customCount = columns.filter((c) => !c.locked).length;

  return (
    <Box>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          p: { xs: 2, md: 4 },
          backgroundImage:
            "radial-gradient(ellipse at 20% 0%, rgba(74,222,128,0.06) 0%, transparent 60%), radial-gradient(ellipse at 80% 100%, rgba(52,211,153,0.05) 0%, transparent 60%)",
        }}
      >
        {/* Header */}
        <Box sx={{ maxWidth: 760, mx: "auto" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "rgba(74,222,128,0.12)",
                border: "1px solid rgba(74,222,128,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TableChartIcon sx={{ color: "primary.main", fontSize: 20 }} />
            </Box>
            <Typography
              variant="h4"
              sx={{ color: "primary.main", letterSpacing: "-0.5px" }}
            >
              Sheet Schema Builder
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mb: 4, pl: "56px" }}
          >
            Define your Google Sheet column structure. Drag to reorder custom
            columns.
          </Typography>

          {/* Sheet Name */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                letterSpacing: 1,
                textTransform: "uppercase",
                mb: 1.5,
                display: "block",
              }}
            >
              Sheet Name
            </Typography>
            <TextField
              fullWidth
              value={sheetName}
              onChange={(e) => setSheetName(e.target.value)}
              placeholder="Enter sheet name..."
              variant="outlined"
              size="small"
              inputProps={{
                style: {
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 600,
                },
              }}
            />
          </Paper>

          {/* Columns */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                Columns · {columns.length} total
              </Typography>
              <Chip
                label="2 mandatory"
                size="small"
                icon={<LockIcon sx={{ fontSize: "13px !important" }} />}
                sx={{
                  bgcolor: "rgba(74,222,128,0.1)",
                  color: "primary.main",
                  border: "1px solid rgba(74,222,128,0.2)",
                  fontSize: 11,
                }}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 1, mb: 1.5, px: 1 }}>
              <Box sx={{ width: 28 }} />
              <Typography
                variant="caption"
                sx={{
                  flex: 2,
                  color: "text.secondary",
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                  fontSize: 10,
                }}
              >
                Column Name
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  flex: 1,
                  color: "text.secondary",
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                  fontSize: 10,
                }}
              >
                Type
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  width: 80,
                  color: "text.secondary",
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                  fontSize: 10,
                }}
              >
                Required
              </Typography>
              <Box sx={{ width: 36 }} />
            </Box>

            <Divider sx={{ borderColor: "rgba(74,222,128,0.25)", mb: 1.5 }} />

            {columns.map((col, idx) => (
              <Fade in key={col.id} timeout={200}>
                <Box
                  draggable={!col.locked}
                  onDragStart={() => handleDragStart(col.id)}
                  onDragOver={(e) => handleDragOver(e, col.id)}
                  onDrop={() => handleDrop(col.id)}
                  onDragEnd={() => {
                    setDragging(null);
                    setDragOver(null);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    p: 1,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: col.locked
                      ? "rgba(74,222,128,0.18)"
                      : dragOver === col.id
                      ? "rgba(74,222,128,0.4)"
                      : "transparent",
                    bgcolor: col.locked
                      ? "rgba(74,222,128,0.04)"
                      : dragging === col.id
                      ? "rgba(74,222,128,0.06)"
                      : "transparent",
                    transition: "all 0.15s ease",
                    cursor: col.locked ? "default" : "grab",
                  }}
                >
                  {/* Drag handle */}
                  <Box
                    sx={{
                      width: 28,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {col.locked ? (
                      <Tooltip title="Mandatory field — cannot be removed or reordered">
                        <LockIcon
                          sx={{
                            fontSize: 15,
                            color: "primary.main",
                            opacity: 0.6,
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <DragIndicatorIcon
                        sx={{
                          fontSize: 18,
                          color: "rgba(74,222,128,0.7)",
                          cursor: "grab",
                        }}
                      />
                    )}
                  </Box>

                  {/* Name */}
                  <TextField
                    size="small"
                    sx={{ flex: 2 }}
                    value={col.name}
                    onChange={(e) =>
                      updateColumn(col.id, "name", e.target.value)
                    }
                    placeholder="column_name"
                    disabled={col.locked}
                    error={!col.locked && col.name.trim() === ""}
                    helperText={
                      !col.locked && col.name.trim() === "" ? "Required" : ""
                    }
                    inputProps={{
                      style: {
                        fontFamily: "'DM Mono', monospace",
                        fontSize: 13,
                      },
                    }}
                  />

                  {/* Type */}
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      value={col.type}
                      onChange={(e) =>
                        updateColumn(col.id, "type", e.target.value)
                      }
                      disabled={col.locked}
                      sx={{
                        fontSize: 13,
                        fontFamily: "'DM Mono', monospace",
                      }}
                    >
                      {COLUMN_TYPES.map((t) => (
                        <MenuItem
                          key={t}
                          value={t}
                          sx={{
                            fontSize: 13,
                            fontFamily: "'DM Mono', monospace",
                          }}
                        >
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Required toggle */}
                  <Box
                    sx={{
                      width: 80,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {col.locked ? (
                      <Chip
                        label="always"
                        size="small"
                        sx={{
                          fontSize: 10,
                          bgcolor: "rgba(74,222,128,0.12)",
                          color: "primary.main",
                          height: 20,
                        }}
                      />
                    ) : (
                      <Switch
                        checked={col.required}
                        onChange={(e) =>
                          updateColumn(col.id, "required", e.target.checked)
                        }
                        size="small"
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "primary.main",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            { bgcolor: "primary.main" },
                        }}
                      />
                    )}
                  </Box>

                  {/* Delete */}
                  <Box
                    sx={{
                      width: 36,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {!col.locked && (
                      <IconButton
                        size="small"
                        onClick={() => removeColumn(col.id)}
                        sx={{
                          color: "rgba(74,222,128,0.7)",
                          "&:hover": {
                            color: "#f87171",
                            bgcolor: "rgba(248,113,113,0.08)",
                          },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Fade>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={addColumn}
              variant="outlined"
              fullWidth
              sx={{
                mt: 1.5,
                borderColor: "rgba(74,222,128,0.25)",
                color: "text.secondary",
                borderStyle: "dashed",
                py: 1,
                fontFamily: "'DM Mono', monospace",
                fontSize: 13,
                "&:hover": {
                  borderColor: "primary.main",
                  color: "primary.main",
                  bgcolor: "rgba(74,222,128,0.04)",
                },
              }}
            >
              Add Column
            </Button>
          </Paper>

          {/* Summary */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: "rgba(74,222,128,0.03)" }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                letterSpacing: 1,
                textTransform: "uppercase",
                mb: 2,
                display: "block",
              }}
            >
              Schema Preview
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {columns.map((col) => (
                <Chip
                  key={col.id}
                  label={
                    <Box
                      component="span"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <span>{col.name || "unnamed"}</span>
                      <Box component="span" sx={{ opacity: 0.5, fontSize: 10 }}>
                        · {col.type}
                      </Box>
                      {col.required && (
                        <Box component="span" sx={{ color: "primary.main" }}>
                          *
                        </Box>
                      )}
                    </Box>
                  }
                  size="small"
                  sx={{
                    bgcolor: col.locked
                      ? "rgba(74,222,128,0.1)"
                      : "rgba(74,222,128,0.06)",
                    border: "1px solid",
                    borderColor: col.locked
                      ? "rgba(74,222,128,0.25)"
                      : "rgba(74,222,128,0.25)",
                    color: col.locked ? "primary.main" : "text.secondary",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 12,
                  }}
                />
              ))}
            </Box>
          </Paper>

          {/* Action */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <GlassButtonLoading
              variant="contained"
              fullWidth
              disabled={!allValid || !sheetName.trim()}
              onClick={handleExport}
              startIcon={<ContentCopyIcon />}
            >
              Copy Schema JSON
            </GlassButtonLoading>
          </Box>

          {!allValid && (
            <Alert
              severity="warning"
              sx={{
                mt: 2,
                bgcolor: "rgba(234,179,8,0.08)",
                border: "1px solid rgba(234,179,8,0.2)",
                color: "#fbbf24",
                fontFamily: "'DM Mono', monospace",
                fontSize: 12,
              }}
            >
              All column names must be filled before exporting.
            </Alert>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, msg: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          severity="success"
          sx={{
            bgcolor: "rgba(74,222,128,0.1)",
            border: "1px solid rgba(74,222,128,0.3)",
            color: "primary.main",
          }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
